import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import DataTable from "react-data-table-component";

const CancelacionDocumentos = () => {
    const [docNum, setDocNum] = useState("");
    const [docType, setDocType] = useState("Factura"); // Por defecto "Factura"
    const [documents, setDocuments] = useState([]);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    // Modal folio assignment state
    const [showFolioModal, setShowFolioModal] = useState(false);
    const [currentDocEntry, setCurrentDocEntry] = useState(null);
    const [folioValue, setFolioValue] = useState("");

    const fetchDocuments = async () => {
        try {
            const response = await apiClient.get("/documents", {
                params: { DocType: docType, DocNum: docNum },
            });
            setDocuments(response.data);
            setError("");
        } catch (err) {
            setError(
                err.response?.data?.error || "Error inesperado al recuperar los documentos."
            );
        }
    };

    const handleCancel = async (docEntry) => {
        try {
            const response = await apiClient.post("/documents/cancel", {
                DocEntry: docEntry,
                DocType: docType,
            });
            setMessage(response.data.message);
            setError("");
            setDocuments(documents.filter((doc) => doc.DocEntry !== docEntry));
        } catch (err) {
            setMessage("");
            setError(
                err.response?.data?.error || "Error inesperado al cancelar el documento."
            );
        }
    };

    const openFolioModal = (docEntry) => {
        setCurrentDocEntry(docEntry);
        // Pre-cargar valor actual si existe
        const existing = documents.find(d => d.DocEntry === docEntry);
        setFolioValue(existing?.FolioNumber ? String(existing.FolioNumber) : "");
        setShowFolioModal(true);
        setError("");
    };

    const closeFolioModal = () => {
        setShowFolioModal(false);
        setCurrentDocEntry(null);
        setFolioValue("");
    };

    const confirmFolioAssignment = async () => {
        if (!currentDocEntry) return;
        const folio = parseInt(folioValue.trim(), 10);
        if (Number.isNaN(folio)) {
            setError("El número de folio debe ser un número válido.");
            return;
        }
        try {
            const response = await apiClient.post("/documents/update", {
                DocEntry: currentDocEntry,
                DocType: docType,
                FolioNumber: folio,
            });
            setMessage(response.data.message || "Folio actualizado correctamente.");
            setError("");
            setDocuments(prev => prev.map(d => d.DocEntry === currentDocEntry ? { ...d, FolioNumber: folio } : d));
            closeFolioModal();
        } catch (err) {
            setMessage("");
            setError(err.response?.data?.error || "Error inesperado al actualizar el folio.");
        }
    };

    const columns = [
        {
            name: "ID Interno",
            selector: (row) => row.DocEntry,
            sortable: true,
            width: "115px",
        },
        {
            name: "Nro. Documento",
            selector: (row) => row.DocNum,
            sortable: true,
            width: "145px",
        },
        {
            name: "Nro. Factura",
            selector: (row) => row.FolioNumber,
            sortable: true,
            width: "115px",
        },
        {
            name: "Cod. Cliente",
            selector: (row) => row.CardCode,
            width: "100px",
        },
        {
            name: "Cliente",
            selector: (row) => row.CardName,
            width: "300px",
        },
        {
            name: "Estado",
            selector: (row) => {
                if (row.DocumentStatus === "bost_Open") {
                    return row.PaidToDate > 0 ? "Importe Aplicado" : "Abierto";
                } else if (row.DocumentStatus === "bost_Close") {
                    return row.Cancelled === "tYES" ? "Cancelado" : "Cerrado";
                }
                return "Desconocido";
            },
            width: "150px",
        },
        {
            name: "Monto Documento",
            selector: (row) => row.DocTotal.toLocaleString("de-DE"), // Separador de miles con puntos
            sortable: true,
            width: "160px",
        },
        {
            name: "Acciones",
            cell: (row) => {
                const canCancel = row.DocumentStatus === "bost_Open" && row.Cancelled !== "tYES";
                const canAssignFolio = row.Cancelled !== "tYES"; // se puede ajustar la lógica según reglas
                return (
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            className={`btn btn-outline-primary ${!canAssignFolio ? "disabled" : ""}`}
                            title="Asignar / editar folio"
                            onClick={() => canAssignFolio && openFolioModal(row.DocEntry)}
                        >
                            {/* Unicode icon fallback */}
                            <span style={{fontSize: "0.85rem"}}>#</span> Folio
                        </button>
                        <button
                            type="button"
                            className={`btn btn-outline-danger ${!canCancel ? "disabled" : ""}`}
                            title={canCancel ? "Cancelar documento" : "No disponible"}
                            onClick={() => canCancel && handleCancel(row.DocEntry)}
                        >
                            <span style={{fontSize: "0.85rem"}}>✖</span> Cancelar
                        </button>
                    </div>
                );
            },
            width: "190px",
        },
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        fetchDocuments();
    };

    return (
        <div className="container mt-5">
            <h2>Cancelación de Documentos</h2>
            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSearch} className="mb-4">
                <div className="row">
                    <div className="col-md-6">
                        <label htmlFor="docType" className="form-label">
                            Tipo de Documento
                        </label>
                        <select
                            className="form-control"
                            id="docType"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                        >
                            <option value="Factura">Factura</option>
                            <option value="Nota de crédito">Nota de Crédito</option>
                            <option value="Entrega">Entrega</option>
                            <option value="Devolucion">Devolucion de Mercaderías</option>
                            <option value="Traslados">Traslados</option>
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="docNum" className="form-label">
                            Número de Documento
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="docNum"
                            value={docNum}
                            onChange={(e) => setDocNum(e.target.value)}
                        />
                    </div>
                </div>
                <button type="submit" className="btn btn-primary mt-3">
                    Buscar
                </button>
            </form>

            <DataTable
                columns={columns}
                data={documents}
                pagination
                highlightOnHover
                responsive
                striped
            />

            {showFolioModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Asignar Folio</h5>
                                <button type="button" className="btn-close" onClick={closeFolioModal} aria-label="Close" />
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Nuevo número de folio</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={folioValue}
                                        onChange={(e) => setFolioValue(e.target.value)}
                                        placeholder="Ingrese folio"
                                        autoFocus
                                    />
                                </div>
                                {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeFolioModal}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={confirmFolioAssignment}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CancelacionDocumentos;